// I02/I04/I06/I08 — industry planners.
import { useMemo, useState } from "react";
import { capacityMap, territoryCoverage, inr } from "./engines";
import { Field, ResultRow, ToolSection, Note, useNumbers, copyText } from "./ui";

function CopyButton({ getText, testid }) {
  const [state, setState] = useState("idle");
  return (
    <button type="button" data-testid={testid}
      onClick={async () => { setState((await copyText(getText())) ? "done" : "fail"); setTimeout(() => setState("idle"), 2500); }}
      className="text-sm font-medium underline underline-offset-4">
      {state === "done" ? "Copied" : state === "fail" ? "Select & copy manually" : "Copy output"}
    </button>
  );
}

/* ---------- I02 FMCG Distribution Expansion Planner ---------- */
export function ToolI02() {
  const [v, set] = useNumbers({ currentOutlets: 40000, targetOutlets: 100000, avgOrderValue: 850, ordersPerOutletMonth: 2, months: 18, outletsPerRepDay: 25, currentReps: 160 });
  const num = (x, d = 0) => { const n = Number(x); return Number.isFinite(n) ? n : d; };
  const r = useMemo(() => {
    const add = Math.max(0, num(v.targetOutlets) - num(v.currentOutlets));
    // reps for steady-state coverage of target outlets (monthly visit each) + ramp build capacity
    const coverage = territoryCoverage({ outlets: num(v.targetOutlets), visitsPerMonth: 1, visitMinutes: 20, travelMinutesPerDay: 120, workDaysPerMonth: 24, workHoursPerDay: 8, currentReps: num(v.currentReps) });
    const newOutletsPerMonth = num(v.months) > 0 ? add / num(v.months) : add;
    const buildRepsPerMonth = num(v.outletsPerRepDay) * 24 > 0 ? newOutletsPerMonth / (num(v.outletsPerRepDay, 25) * 24) : 0;
    const phases = [
      { phase: "Months 1–6", outlets: Math.round(add * 0.35), focus: "Highest-potential towns first; distributor appointments precede coverage" },
      { phase: "Months 7–12", outlets: Math.round(add * 0.4), focus: "Second-tier towns; wholesale push; beat discipline audits" },
      { phase: "Months 13–18", outlets: add - Math.round(add * 0.35) - Math.round(add * 0.4), focus: "Rural/long-tail via sub-stockists; productivity per outlet" },
    ];
    return { add, newOutletsPerMonth: Math.round(newOutletsPerMonth), buildRepsNeeded: Math.ceil(buildRepsPerMonth), coverage, phases };
  }, [v]);
  const monthlyRevenueAtTarget = num(v.targetOutlets) * num(v.avgOrderValue) * num(v.ordersPerOutletMonth);
  return (
    <div data-testid="tool-I02" className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Current active outlets" value={v.currentOutlets} onChange={set("currentOutlets")} testid="i02-current" />
          <Field label="Target outlets" value={v.targetOutlets} onChange={set("targetOutlets")} testid="i02-target" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Avg order value (₹)" value={v.avgOrderValue} onChange={set("avgOrderValue")} testid="i02-aov" />
          <Field label="Orders / outlet / month" value={v.ordersPerOutletMonth} onChange={set("ordersPerOutletMonth")} testid="i02-orders" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Build months" value={v.months} onChange={set("months")} testid="i02-months" />
          <Field label="New outlets / rep / day" value={v.outletsPerRepDay} onChange={set("outletsPerRepDay")} testid="i02-perrep" />
          <Field label="Current field reps" value={v.currentReps} onChange={set("currentReps")} testid="i02-reps" />
        </div>
      </div>
      <div>
        <div data-testid="i02-result">
          <ResultRow label="Outlets to add" value={r.add.toLocaleString("en-IN")} />
          <ResultRow label="Build pace" value={`${r.newOutletsPerMonth.toLocaleString("en-IN")} outlets / month`} />
          <ResultRow label="Reps needed just to build" value={String(r.buildRepsNeeded)} />
          <ResultRow label="Reps for steady-state coverage at target" value={String(r.coverage.requiredRepsCeil)} strong />
          <ResultRow label="Current reps" value={String(v.currentReps)} tone={num(v.currentReps) < r.coverage.requiredRepsCeil ? "bad" : "good"} />
          <ResultRow label="Monthly revenue at full target" value={inr(monthlyRevenueAtTarget)} strong />
        </div>
        <div className="mt-6 space-y-3">
          {r.phases.map((p) => (
            <div key={p.phase} className="border border-zinc-200 p-4 grid md:grid-cols-12 gap-2" data-testid={`i02-${p.phase.split(" ")[1]}`}>
              <span className="md:col-span-3 text-xs font-semibold uppercase tracking-wide pt-0.5">{p.phase}</span>
              <span className="md:col-span-3 text-sm font-medium tabular-nums">+{p.outlets.toLocaleString("en-IN")} outlets</span>
              <span className="md:col-span-6 text-xs text-zinc-500">{p.focus}</span>
            </div>
          ))}
        </div>
        <Note tone="warn">Numeric distribution without servicing is a vanity metric: an outlet visited once and abandoned is worse than none. Coverage capacity (not ambition) sets the honest target.</Note>
        <Note>Coverage assumptions: one 20-minute visit per outlet per month, 24 field days a month, 8-hour days with 2 hours of daily travel per rep (≈18 productive visits a day). Your beat economics may differ — adjust the inputs and read the coverage row, not the ambition.</Note>
      </div>
    </div>
  );
}

/* ---------- I04 Manufacturing Capacity-to-Revenue Finder ---------- */
const I04_DEFAULT = [
  { segment: "OEM contracts", pricePerUnit: 500, variableCostPerUnit: 380, demandUnits: 2000 },
  { segment: "Export orders", pricePerUnit: 620, variableCostPerUnit: 400, demandUnits: 5000 },
  { segment: "Job work / tolling", pricePerUnit: 350, variableCostPerUnit: 300, demandUnits: 4000 },
];
export function ToolI04() {
  const [v, set] = useNumbers({ monthlyCapacityUnits: 10000, utilizationPct: 70 });
  const [cands, setCands] = useState(I04_DEFAULT);
  const r = useMemo(() => capacityMap({ monthlyCapacityUnits: v.monthlyCapacityUnits, utilizationPct: v.utilizationPct, candidates: cands }), [v, cands]);
  const setC = (i, k) => (val) => setCands((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: val } : x)));
  return (
    <div data-testid="tool-I04">
      <div className="grid grid-cols-2 gap-4 max-w-md">
        <Field label="Plant capacity (units / month)" value={v.monthlyCapacityUnits} onChange={set("monthlyCapacityUnits")} testid="i04-cap" />
        <Field label="Current utilisation %" value={v.utilizationPct} onChange={set("utilizationPct")} testid="i04-util" />
      </div>
      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="i04-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b border-black">
              {["Demand segment", "Price / unit ₹", "Variable cost / unit ₹", "Demand (units / mo)"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {cands.map((c, i) => (
              <tr key={i} className="border-b border-black/10" data-testid={`i04-row-${i}`}>
                <td className="py-2 pr-3"><input value={c.segment} onChange={(e) => setC(i, "segment")(e.target.value)} className="w-40 bg-transparent outline-none border-b border-transparent focus:border-black text-sm" /></td>
                {["pricePerUnit", "variableCostPerUnit", "demandUnits"].map((k) => (
                  <td key={k} className="py-2 pr-3"><input type="number" value={c[k]} onChange={(e) => setC(i, k)(e.target.value)} className="w-24 border border-zinc-200 px-1.5 py-1 text-sm outline-none focus:border-black" data-testid={`i04-${k}-${i}`} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={() => setCands((s) => [...s, { segment: `Segment ${s.length + 1}`, pricePerUnit: 0, variableCostPerUnit: 0, demandUnits: 0 }])} className="mt-4 text-sm font-medium underline underline-offset-4" data-testid="i04-add">+ Add segment</button>
      <div className="mt-6 max-w-lg" data-testid="i04-result">
        <ResultRow label="Idle capacity" value={`${r.idleUnits.toLocaleString("en-IN")} units / month`} strong tone={r.idleUnits > 0 ? "bad" : "good"} />
        {r.plan.map((p) => (
          <ResultRow key={p.segment} label={`${p.segment} — allocate ${p.allocatedUnits.toLocaleString("en-IN")} u`} value={`${inr(p.contributionPerUnit)}/u · ${inr(p.contributionPerUnit * p.allocatedUnits)}`} tone={p.allocatedUnits > 0 ? "good" : undefined} />
        ))}
        <ResultRow label="Absorbable contribution / month" value={inr(r.totalAbsorbableContribution)} strong />
        {r.unallocatedIdle > 0 && <ResultRow label="Still idle after allocation" value={`${r.unallocatedIdle.toLocaleString("en-IN")} units`} tone="bad" />}
      </div>
      <Note>Idle capacity is unsold revenue with a carrying cost. Allocate to the highest contribution per unit first — but a segment that fills the plant at thin margin can still be right if it covers fixed costs while you build better demand.</Note>
    </div>
  );
}

/* ---------- I06 Sponsorship Rate Card Builder ---------- */
const I06_DEFAULT = [
  { name: "Title sponsor", slots: 1, price: 1500000, costToServe: 250000, includes: "Naming rights, logo lockup everywhere, 4 segments, first right of renewal" },
  { name: "Powered-by sponsor", slots: 2, price: 600000, costToServe: 120000, includes: "2 segments, logo on key assets, social package" },
  { name: "Segment sponsor", slots: 4, price: 250000, costToServe: 60000, includes: "1 segment, lower-third presence, tagged posts" },
];
export function ToolI06() {
  const [pkgs, setPkgs] = useState(I06_DEFAULT);
  const [audience, setAudience] = useState({ reach: 500000, profile: "Urban 25–44, decision-makers, 62% male", engagement: "4.1% avg engagement, 38% completion on video" });
  const setP = (i, k) => (e) => setPkgs((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: e.target.value } : x)));
  const totalIfSold = pkgs.reduce((s, p) => s + (Number(p.price) || 0) * (Number(p.slots) || 0), 0);
  const totalCost = pkgs.reduce((s, p) => s + (Number(p.costToServe) || 0) * (Number(p.slots) || 0), 0);
  return (
    <div data-testid="tool-I06">
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="space-y-4">
          <div className="border border-zinc-200 p-4 space-y-3">
            <div className="text-sm font-medium">The property (what sponsors are buying)</div>
            <Field label="Qualified reach / audience size" value={audience.reach} onChange={(v) => setAudience((s) => ({ ...s, reach: v }))} testid="i06-reach" />
            <Field label="Audience profile" type="text" value={audience.profile} onChange={(v) => setAudience((s) => ({ ...s, profile: v }))} testid="i06-profile" />
            <Field label="Engagement proof" type="text" value={audience.engagement} onChange={(v) => setAudience((s) => ({ ...s, engagement: v }))} testid="i06-eng" />
          </div>
          {pkgs.map((p, i) => (
            <div key={i} className="border border-zinc-200 p-4" data-testid={`i06-pkg-${i}`}>
              <input value={p.name} onChange={setP(i, "name")} className="w-full text-sm font-medium outline-none border-b border-transparent focus:border-black pb-1" />
              <div className="mt-3 grid grid-cols-3 gap-3">
                <Field label="Slots" value={p.slots} onChange={(v) => setPkgs((s) => s.map((x, xi) => (xi === i ? { ...x, slots: v } : x)))} testid={`i06-slots-${i}`} />
                <Field label="Price (₹)" value={p.price} onChange={(v) => setPkgs((s) => s.map((x, xi) => (xi === i ? { ...x, price: v } : x)))} testid={`i06-price-${i}`} />
                <Field label="Cost to serve (₹)" value={p.costToServe} onChange={(v) => setPkgs((s) => s.map((x, xi) => (xi === i ? { ...x, costToServe: v } : x)))} testid={`i06-cost-${i}`} />
              </div>
              <textarea value={p.includes} onChange={setP(i, "includes")} rows={2} className="mt-3 w-full border border-zinc-200 p-2 text-sm outline-none focus:border-black" placeholder="What's included…" />
            </div>
          ))}
          <button type="button" onClick={() => setPkgs((s) => [...s, { name: `Package ${s.length + 1}`, slots: 1, price: 0, costToServe: 0, includes: "" }])} className="text-sm font-medium underline underline-offset-4" data-testid="i06-add">+ Add package</button>
        </div>
        <div>
          <div className="border-2 border-black p-6 bg-white" data-testid="i06-preview">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Rate card · {audience.reach.toLocaleString("en-IN")} qualified reach</div>
            <p className="mt-2 text-xs text-zinc-500">{audience.profile} · {audience.engagement}</p>
            <div className="mt-4 space-y-4">
              {pkgs.map((p, i) => {
                const margin = (Number(p.price) || 0) - (Number(p.costToServe) || 0);
                return (
                  <div key={i} className="border-b border-black/10 pb-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-sm font-medium">{p.name} <span className="text-xs text-zinc-400">× {p.slots}</span></span>
                      <span className="text-sm font-semibold tabular-nums">{inr(Number(p.price) || 0)}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">{p.includes}</p>
                    <p className={`text-xs mt-1 ${margin < 0 ? "text-red-700 font-medium" : "text-zinc-400"}`}>Margin per slot: {inr(margin)}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-4">
              <ResultRow label="If fully sold" value={inr(totalIfSold)} strong />
              <ResultRow label="Delivery cost" value={inr(totalCost)} />
              <ResultRow label="Sponsorship margin" value={inr(totalIfSold - totalCost)} strong tone={totalIfSold - totalCost > 0 ? "good" : "bad"} />
            </div>
          </div>
          <div className="mt-4"><CopyButton testid="i06-copy" getText={() => `SPONSORSHIP RATE CARD\nAudience: ${audience.reach} — ${audience.profile}\nProof: ${audience.engagement}\n\n${pkgs.map((p) => `${p.name} (${p.slots} slots) — ₹${p.price}\n  Includes: ${p.includes}`).join("\n\n")}`} /></div>
          <Note>Sponsors buy audiences, not logos. The profile and proof lines carry the sale; the package ladder gives the brand manager a 'yes' at their budget. Report results to sponsors within a week of delivery — renewals live there.</Note>
        </div>
      </div>
    </div>
  );
}

/* ---------- I08 Professional Services Authority Sprint (30 days) ---------- */
const I08_DAYS = [
  ["Days 1–3", "Position", ["Pick the one problem you want to be known for (not five)", "Write your point of view: what everyone gets wrong about it", "Draft the diagnostic question prospects should ask themselves"]],
  ["Days 4–7", "Assets", ["Turn the point of view into a signature post + a one-page framework", "Update your profile to say who you help and what changes", "Set up a booking link with 3 slots a week"]],
  ["Week 2", "Presence", ["Publish 3 posts from the framework — problem, cost, the way out", "Send 10 personal notes to past clients: what changed since, one insight each", "Comment meaningfully on 5 buyer posts daily"]],
  ["Week 3", "Proof", ["Write one detailed case: problem, what you did, the numbers", "Offer 5 free 30-minute diagnostics to ideal prospects", "Turn every good question you get into next week's post"]],
  ["Week 4", "Pipeline", ["Follow up every diagnostic with a one-page proposal within 48h", "Publish the month-in-review: what you learned, what you offer", "Review the metrics: profile visits, conversations started, calls booked"]],
];
export function ToolI08() {
  const [done, setDone] = useState({});
  const [metrics, setMetrics] = useState({ visits: "", conversations: "", calls: "" });
  const totalTasks = I08_DAYS.reduce((s, [, , t]) => s + t.length, 0);
  const completed = Object.values(done).filter(Boolean).length;
  return (
    <div data-testid="tool-I08">
      <p className="text-sm text-zinc-600 mb-5">Thirty days from invisible expertise to a consultation pipeline. Tick tasks as you ship them; log the three numbers at the end of each week.</p>
      <div className="space-y-4">
        {I08_DAYS.map(([phase, theme, tasks], pi) => (
          <div key={phase} className="border border-zinc-200 p-5" data-testid={`i08-phase-${pi}`}>
            <div className="flex items-baseline gap-3">
              <span className="text-xs font-semibold uppercase tracking-wide">{phase}</span>
              <span className="font-serif-display text-lg">{theme}</span>
            </div>
            <div className="mt-3 space-y-2">
              {tasks.map((t, ti) => {
                const id = `${pi}-${ti}`;
                return (
                  <label key={id} className={`flex items-start gap-3 p-2.5 border transition-colors cursor-pointer ${done[id] ? "border-black bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`} data-testid={`i08-task-${id}`}>
                    <input type="checkbox" checked={!!done[id]} onChange={() => setDone((s) => ({ ...s, [id]: !s[id] }))} className="mt-0.5 w-4 h-4 accent-black" />
                    <span className="text-sm text-zinc-700">{t}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <ToolSection title="The only three numbers that matter" testid="i08-metrics">
        <div className="grid grid-cols-3 gap-4 max-w-lg">
          <Field label="Profile visits" value={metrics.visits} onChange={(v) => setMetrics((s) => ({ ...s, visits: v }))} testid="i08-m-visits" />
          <Field label="Conversations started" value={metrics.conversations} onChange={(v) => setMetrics((s) => ({ ...s, conversations: v }))} testid="i08-m-conv" />
          <Field label="Calls booked" value={metrics.calls} onChange={(v) => setMetrics((s) => ({ ...s, calls: v }))} testid="i08-m-calls" />
        </div>
        <div className="mt-4 max-w-md" data-testid="i08-result">
          <ResultRow label="Sprint progress" value={`${completed} / ${totalTasks} tasks`} strong={completed === totalTasks} />
        </div>
        <Note>Follower counts are decoration. The chain that pays: visits → conversations → calls. If visits rise but conversations don't, the positioning is interesting but not relevant; if conversations happen but calls don't, the offer needs sharpening.</Note>
      </ToolSection>
    </div>
  );
}
